import React from 'react';
import { ActionIcon } from '@mantine/core';
import { MdAdd, MdRemove } from 'react-icons/md';
import Button from '@srm/ui/button';
import { IMatrixLot, IMatrixParticipant } from '../WinnerSelection2.types';
import { formatMatrixMessage, formatMoney } from '../utils/matrixView';

interface IProps {
    lot: IMatrixLot;
    rowId: string;
    participants: IMatrixParticipant[];
    participantIds: string[];
    positionsCount: number;
    collapsed: boolean;
    priceVisible: boolean;
    /** Общий запрет изменений объяснён баннером — причины по ячейкам не повторяем. */
    canEdit: boolean;
    busy: boolean;
    onToggle: (lotKey: string) => void;
    onDecideLot: (lot: IMatrixLot, participant: IMatrixParticipant, trigger: HTMLElement) => void;
}

const LotRow: React.FC<IProps> = ({
    lot,
    rowId,
    participants,
    participantIds,
    positionsCount,
    collapsed,
    priceVisible,
    canEdit,
    busy,
    onToggle,
    onDecideLot,
}) => (
    <tr className="ws2-lot">
        <th scope="row" id={rowId}>
            <div className="ws2-lot__name">
                <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    className="ws2-lot__toggle"
                    aria-expanded={!collapsed}
                    aria-label={`${collapsed ? 'Развернуть' : 'Свернуть'} лот «${lot.title}»`}
                    onClick={() => onToggle(lot.key)}
                >
                    {collapsed ? <MdAdd size="18px" aria-hidden /> : <MdRemove size="18px" aria-hidden />}
                </ActionIcon>
                <span>{lot.title}</span>
            </div>
            <div className="ws2-lot__sub">
                {positionsCount} поз.
                {lot.submissionModeName ? ` · ${lot.submissionModeName}` : ''}
            </div>
        </th>
        {participants.map((participant, index) => {
            const summary = lot.participants.find((item) => item.participantKey === participant.key);
            if (!summary || summary.offeredPositions === 0) {
                return (
                    <td key={participant.key} headers={`${rowId} ${participantIds[index]}`}>
                        <span className="ws2-lot__reason">Нет предложений в лоте</span>
                    </td>
                );
            }
            return (
                <td key={participant.key} headers={`${rowId} ${participantIds[index]}`}>
                    {priceVisible && summary.totals.length > 0 ? (
                        summary.totals.map((total) => (
                            <div className="ws2-lot__total" key={total.currency}>
                                {summary.mainPositions > 0 && formatMoney(total.main, total.currency)}
                                {summary.mainPositions > 0 && summary.reservePositions > 0 && ' · '}
                                {summary.reservePositions > 0 && `резерв ${formatMoney(total.reserve, total.currency)}`}
                            </div>
                        ))
                    ) : (
                        <div className="ws2-lot__total">{priceVisible ? 'Объём не выбран' : 'Суммы скрыты'}</div>
                    )}
                    <div className="ws2-lot__counts">
                        Основных {summary.mainPositions} · резервных {summary.reservePositions} из {positionsCount}
                    </div>
                    {summary.canDecideLot || summary.canResetLot ? (
                        <div className="ws2-lot__action">
                            <Button
                                variant="outline-neutral"
                                size="sm"
                                disabled={busy}
                                onClick={(event) => onDecideLot(lot, participant, event.currentTarget)}
                                aria-label={`Оценить весь лот «${lot.title}»: ${participant.name}`}
                            >
                                Оценить весь лот
                            </Button>
                        </div>
                    ) : (
                        canEdit &&
                        summary.lotDecisionReason && <div className="ws2-lot__reason">{formatMatrixMessage(summary.lotDecisionReason)}</div>
                    )}
                </td>
            );
        })}
    </tr>
);

export default React.memo(LotRow);
